import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { MatCard } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { v4 as uuid } from 'uuid';
import { getColor, ColorProp } from '../../../layout/Colors';
import { DataTableFilter } from '../../../shared/components/data-table/filters/components/DataTableFilter';
import { VerificationPackageBasicInfoDialogComponent } from '../verification-package-detail/components/basic-info-dialog/basic-info-dialog.component';
import { VerificationPackageColumns, VerificationPackageDetail, VerificationPackageRow } from '../models/VerificationPackage';
import { VerificationPackagesFilterStateService } from '../services/verification-packages-filter-state.service';
import { VerificationPackagesTableMessageService } from '../services/verification-packages-table-message.service';
import { VerificationPackagesService } from '../services/verification-packages.service';
import { VerificationPackagesFilterStatusComponent } from './components/filters/verification-packages-filter-status/verification-packages-filter-status.component';
import { Logger } from '../../../infrastructure/logging/logger';
import { PillComponent } from '../../../shared/components/pill/pill.component';

@Component({
  selector: 'app-verification-packages',
  templateUrl: './verification-packages.component.html',
  styleUrls: ['./verification-packages.component.scss'],
  standalone: true,
  imports: [
    CommonModule, 
    MatButtonModule, 
    MatIcon, 
    MatCard, 
    MatTableModule,
    MatFormFieldModule,
    MatSortModule,
    MatPaginatorModule,
    MatInputModule,
    FormsModule,
    MatProgressSpinner,
    PillComponent
  ]
})
export class VerificationPackagesComponent implements OnInit, AfterViewInit {

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort: MatSort = new MatSort;

  STATUS_ACTIVE = 1;
  STATUS_INACTIVE = 2;

  activeFilter: boolean = false;
  inactiveFilter: boolean = false;
  displayedColumns = VerificationPackageColumns;
  displayedColumnNames: string[] = [];
  searchKeyword: string = '';

  filterDefinitions: DataTableFilter[] = [
    { id: uuid(), name: 'Status', shortName: 'Status', component: VerificationPackagesFilterStatusComponent }
  ];

  loading = new BehaviorSubject<boolean>(false);
  verificationPackages: VerificationPackageDetail[] = [];
  mappedVerificationPackages: VerificationPackageDetail[] = [];
  dataSource = new MatTableDataSource<VerificationPackageRow>();
  tableLength = 0;
  pageSize = 10;
  pageNumber: number = 1;

  initialKeyword = new BehaviorSubject<string>('');
  initialSort = new BehaviorSubject<Sort | undefined>(undefined);
  initialPageSize = new BehaviorSubject<number>(10);
  initialPageNumber = new BehaviorSubject<number>(1);

  constructor(
    private verificationPackagesService: VerificationPackagesService,
    private verificationPackagesFilterStateService: VerificationPackagesFilterStateService,
    private verificationPackagesMessageService: VerificationPackagesTableMessageService,
    private logService: Logger,
    private dialog: MatDialog,
    private matSnackbar: MatSnackBar,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit() {
    this.setupAfterContextLoaded();
    this.initTableColumns();
    this.loadPage();

    const storedItemsPerPage = localStorage.getItem('verifPackageItemsPerPage');
    if (storedItemsPerPage) {
      this.initialPageSize.next(parseInt(storedItemsPerPage, 10));
    } else {
      this.initialPageSize.next(10);
    }
  }

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;

    this.initialPageSize?.subscribe(pageSize => {
      this.pageSize = pageSize;
      this.paginator.pageSize = pageSize;
      this.cdr.detectChanges();
    });

    this.initialPageNumber?.subscribe(pageNumber => {
      if (pageNumber != 1) {
        this.pageNumber = pageNumber;
        this.paginator.pageIndex = pageNumber;
      } else {
        this.pageNumber = 1;
      }
      this.cdr.detectChanges();
    });

    this.initialKeyword?.subscribe(keyword => {
      this.searchKeyword = keyword;
      this.cdr.detectChanges();
    });

    this.initialSort?.subscribe(sort => {
      if (!sort) {
        return;
      }

      this.setSort(sort);
      this.cdr.detectChanges();
    });
  }

  setSort(sort: Sort) {
    this.sort.sort({ id: sort.active, start: sort.direction, disableClear: false });
  }

  initTableColumns(): void {
    if (this.displayedColumns === null) {
      this.displayedColumns = [];
    }
    this.displayedColumnNames = this.displayedColumns.map(c => c.column);
  }

  setupAfterContextLoaded() {
    this.setupFilterEventHandler();
  }

  setupFilterEventHandler() {
    this.verificationPackagesMessageService.applyFilterEvent.subscribe(_ => {
      const status = this.verificationPackagesFilterStateService.status;
      this.handleStatusFilterUpdate(Number(status));
    });
  }

  handleStatusFilterUpdate(status: number) {
    switch(status) {
      case 1:
        this.activeFilter = true;
        this.inactiveFilter = false;
        this.updateDataSource(this.mappedVerificationPackages.filter(verifPackage => verifPackage.pillStatus === 'Active'));
        this.setPagination();
        break;
      case 2:
        this.activeFilter = false;
        this.inactiveFilter = true;
        this.updateDataSource(this.mappedVerificationPackages.filter(verifPackage => verifPackage.pillStatus === 'Inactive'));
        this.setPagination();
        break;
      default:
        this.activeFilter = false;
        this.inactiveFilter = false;
        this.dataSource.data = this.mappedVerificationPackages;
        this.tableLength = this.mappedVerificationPackages.length;
        this.setPagination();
        break;
    }
  }

  setPagination() {
    this.dataSource = new MatTableDataSource(this.dataSource.data.slice(0, 10));
  }

  async loadPage() {
    try {
      this.loading.next(true);
      this.dataSource.data = [];

      this.verificationPackages = await this.verificationPackagesService.getVerificationPackages();
      this.mapVerificationPackageDatasource(this.verificationPackages);
    } catch (e) {
      this.logService.handleError(e, 'An error occurred while retrieving Verification Packages', 'An error occurred while retrieving Verification Packages');
    } finally {
      this.loading.next(false);
    }
  }

  async mapVerificationPackageDatasource(verificationPackages: VerificationPackageDetail[]) {
    this.dataSource.data = [];
    this.mappedVerificationPackages = verificationPackages.map((verifPackage: any) => this.buildVerificationRow(verifPackage));
    this.updateDataSource(this.mappedVerificationPackages);
  }

  buildVerificationRow = (verificationPackage: VerificationPackageDetail): VerificationPackageRow => {
    let statusCode;
    if (verificationPackage.status_id === 1) {
      statusCode = "Active"
    } else {
      statusCode = "Inactive"
    }

    return {
      id: verificationPackage.id,
      verification_package_id: verificationPackage.verification_package_id,
      organization_id: verificationPackage.organization_id,
      name: verificationPackage.name,
      description: verificationPackage.description,
      pillStatus: statusCode
    }
  }

  updateDataSource(mappedVerificationPackages:VerificationPackageDetail[]) {
    this.dataSource.data = mappedVerificationPackages;
    this.tableLength = mappedVerificationPackages.length;
  }

  getPackagePrimaryColorForTable(primaryflag: string): string {
    if (primaryflag === "Active") {
      return getColor(ColorProp.PropertyStatusActive);
    } else {
      return '#d3d3d3';
    }
  }

  onNewVerificationPackageClick(): void {
    this.openVerificationPackageBasicInfoDialog();
  }

  openVerificationPackageBasicInfoDialog() {
    const basicInfoDialogRef = this.dialog.open(VerificationPackageBasicInfoDialogComponent, {
      disableClose: true,
      width: '70%'
    });
    basicInfoDialogRef.afterClosed().subscribe((payload) => {
      if (payload) {
        this.verificationPackagesService.createVerificationPackage(payload).then(_ => {
          this.loadPage();
        });
      }
    });
  }

  onPageEvent(event: PageEvent): void {
    this.pageNumber = event.pageIndex + 1;
    localStorage.setItem('verifPackageItemsPerPage', event.pageSize.toString());
  }

  applySearch(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();

    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  clearSearch() {
    this.searchKeyword = '';
    this.dataSource = new MatTableDataSource(this.mappedVerificationPackages);
    this.ngAfterViewInit();
  }

  onGoToPageClick() {
    const max = Math.trunc(Math.ceil(this.paginator.length / this.paginator.pageSize));
    if (this.pageNumber < 1) {
      this.resetPaginator(1, true);
      this.displayInvalidPaginatorPageIndexError();
    } else if (this.pageNumber > max) {
      this.resetPaginator(max, false);
      this.displayInvalidPaginatorPageIndexError();
    } else {
      this.paginator.pageIndex = this.pageNumber - 1;
      this.paginator.page.next({
        pageIndex: this.pageNumber - 1,
        pageSize: this.paginator.pageSize,
        length: this.paginator.length
      });
    }
  }

  resetPaginator(pageNumber: number, goToFirstPage: boolean) {
    this.pageNumber = pageNumber;

    if (this.paginator) {
      if (goToFirstPage) {
        this.paginator.firstPage();
      } else {
        this.paginator.lastPage();
      }
    }
  }

  displayInvalidPaginatorPageIndexError() {
    this.matSnackbar.open('Invalid page number', 'OK', { duration: 3000 });
  }

  getPackageStatusColor(status: string): string {
    if (status === "Active"){
      return getColor(ColorProp.PropertyStatusActive);
    } else {
      return '#d3d3d3';
    }
  }

  onRowClick(row: any): void {
    this.router.navigate([`/verification-packages/detail/${row['id']}`]);
  }
}